import React, { Component } from 'react';
import i18next from 'i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Unibrow from 'components/Unibrow';
import Link from 'components/Link';
import SocialShareList from 'components/SocialShareList';

import * as CustomPropTypes from 'lib/CustomPropTypes';

import { attribution } from 'lib/social';
import { getFormattedDateString, formatDuration } from 'lib/DateTime';
import { getSlugForVertical } from 'lib/vertical';

import $t from 'lib/trackingService';

import styles from './styles.module.scss';

const EVENT_NAME = 'mbt_video_socialShare';
const block = 'videoDetails';

$t('register', EVENT_NAME);

class VideoDetails extends Component {
  static propTypes = {
    vertical: PropTypes.string.isRequired,
    video: CustomPropTypes.video.isRequired,
    social: CustomPropTypes.socialMediaProfiles.isRequired,
    relatedArticle: CustomPropTypes.relatedVideoArticle,
    isChromeless: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    relatedArticle: {},
  };

  constructor(props) {
    super(props);
    this.state = {
      readMoreSelected: false,
      sponsorMoreSelected: false,
    };
  }

  readMoreClick = () => {
    const { readMoreSelected } = this.state;
    this.setState({ readMoreSelected: !readMoreSelected });
  }

  related = () => {
    const { relatedArticle: { headline, ecommerceEnabled, url } = {}, vertical } = this.props;

    if (!headline) {
      return null;
    }

    const { primary: primaryHeadline } = headline;

    return (
      <div className={styles.relatedArticle} data-test="related-container">
        <Link
          href={`${url}?icid=canonical_related`}
          data-test="related-link"
          rel="noopener noreferrer"
          target="_blank"
        >
          <p
            className={classNames(
              styles.article,
              {
                'color-secondary': vertical === 'today',
                'color-knockout-primary f4 lh-copy': vertical !== 'today',
              },
            )}
            data-test="related-text"
          >
            <span
              className={classNames(
                styles.icon,
                'icon',
                {
                  'icon-dot': !ecommerceEnabled,
                  'icon-shop': ecommerceEnabled,
                  'color-primary': vertical === 'today' || vertical === 'msnbc',
                  'color-secondary': vertical === 'news',
                },
              )}
            />
            {ecommerceEnabled && (
              <span className="b mr1" data-test="related-prefix">
                {i18next.t('SHOP')}
                :
              </span>
            )}
            <span>
              {primaryHeadline}
            </span>
          </p>
        </Link>
      </div>
    );
  }

  sponsorMoreClick = () => {
    const { sponsorMoreSelected } = this.state;
    this.setState({ sponsorMoreSelected: !sponsorMoreSelected });
  }

  readMore() {
    const { state, readMoreClick } = this;
    const label = state.readMoreSelected ? i18next.t('Less') : i18next.t('More');

    return (
      <button
        className={styles.dekReadMore}
        onClick={readMoreClick}
        type="button"
      >
        { i18next.t('Read') }
        {' '}
        {label}
      </button>
    );
  }

  title() {
    const { video } = this.props;
    const { headline } = video;
    return (
      <h1
        className={styles.title}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: headline.primary }}
      />
    );
  }

  social() {
    const { video, social, vertical } = this.props;
    const shareAttrib = attribution(social, vertical);
    const { headline, url } = video;

    const videoId = video.id;
    const verticalSlug = getSlugForVertical(vertical);

    return (
      <SocialShareList
        placement="video"
        facebookHref={`https://www.facebook.com/sharer/sharer.php?u=${url.primary}`}
        twitterHref={`https://twitter.com/intent/tweet?text=${encodeURIComponent(headline.social)}&via=${shareAttrib}&url=${url.primary}&original_referer=${url.primary}`}
        emailHref={`mailto:?subject=${headline.primary}&body=${url.primary}`}
        embedHref={`/${verticalSlug}/embedded-video/${videoId}`}
        classes="df justify-start mt6"
        tracking={EVENT_NAME}
        displayOptions={{
          isKnockout: vertical !== 'today' ? true : null,
          useColoredIcons: vertical === 'today' ? true : null,
        }}
      />
    );
  }

  dek() {
    const { isChromeless, video } = this.props;
    const { description, datePublished } = video;
    const { readMoreSelected } = this.state;

    const className = classNames(styles.dek, { [styles.dekOpen]: readMoreSelected });

    return (
      <div className={className}>
        <p className={styles.dekText}>
          {description.primary}
          <span className={styles.byline}>
            {getFormattedDateString(datePublished, null, {}, i18next)}
          </span>
        </p>
        {!isChromeless && this.related()}
      </div>
    );
  }

  sponsor() {
    const { state, sponsorMoreClick } = this;
    const labelText = i18next.t('What\'s this?');
    const labelTitle = i18next.t('Click to see more info');
    const { video, video: { unibrow } } = this.props;

    return (
      <div
        className={classNames(styles.categoryAndSponsor, {
          [styles.noSponsor]: !video.nativeAd,
        }, 'db-m df flex-column')}
      >
        <Unibrow
          className={styles.category}
          unibrow={unibrow}
        />
        {video.nativeAd
          && (
            <div>
              <section className={classNames(styles.sponsorTop, 'db-m df justify-start items-center')}>
                <div className={styles.sponsorLabel}>
                  {/* eslint-disable jsx-a11y/no-static-element-interactions,
                  jsx-a11y/click-events-have-key-events, jsx-a11y/anchor-is-valid */}
                  <a
                    className={styles.sponsorMoreLink}
                    title={labelTitle}
                    onClick={sponsorMoreClick}
                  >
                    {labelText}
                  </a>
                  {/* eslint-enable jsx-a11y/click-events-have-key-events,
                  jsx-a11y/click-events-have-key-events, jsx-a11y/anchor-is-valid */}
                </div>
              </section>
              <div
                className={classNames(styles.sponsorMore, {
                  [styles.sponsorMoreSelected]: state.sponsorMoreSelected,
                })}
              >
                <p>
                  { i18next.t('This content was paid for by an advertiser and created by the NBC News Brand Studio. The NBC News editorial organization was not involved in its creation or production. This content represents the views and opinions of the advertiser, who is responsible for all of the material contained therein. Links are not endorsements by NBC News.') }
                </p>
                {/* eslint-disable jsx-a11y/no-static-element-interactions,
                 jsx-a11y/click-events-have-key-events */}
                <span
                  className={classNames(styles.sponsorMoreClose, 'icon', 'icon-close')}
                  onClick={sponsorMoreClick}
                />
                {/* eslint-disable jsx-a11y/no-static-element-interactions,
                 jsx-a11y/click-events-have-key-events */}
              </div>
            </div>
          )}
      </div>
    );
  }

  videoDuration() {
    const { video: { duration } } = this.props;
    const formattedDuration = formatDuration(duration);

    return (
      <span className={`${block}__duration`} data-test="videoDetails">{formattedDuration}</span>
    );
  }

  render() {
    const { video: { nativeAd } } = this.props;
    return (
      <div className="layout-grid-container">
        <div className={classNames(styles.info, 'mh5 mh0-m', { [styles.sponsored]: nativeAd })}>
          {this.sponsor()}
          <div className={styles.titleAndShareAndDek}>
            <div className={styles.titleAndShare}>
              {this.title()}
              {this.videoDuration()}
              {this.social()}
            </div>
            {this.dek()}
            {this.readMore()}
          </div>
        </div>
      </div>
    );
  }
}

export default VideoDetails;
